import { Component, computed, Input, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';
import { NoticeAction, NoticeModuleName, NoticeRetrievalMethod } from '../../../../core/components/bx-notices/notices.enum';
import { RouterStateService } from '../../../../core/routing/router-state.service';
import { DialogService } from '../../../../core/services/dialog.service';
import { EstimateEventsService } from '../../../../core/services/events/estimate-events.service';
import { LeadsService } from '../../../../core/services/leads.service';
import { FeatureService } from '../../../../core/services/permission/feature.service';
import { PermissionService } from '../../../../core/services/permission/permission.service';
import { Estimate, Job, JobPayment, JobVariation } from '../../../../data/models';
import { RepoJobsService } from '../../../../data/repositories/repo-jobs.service';
import { RepoSettingsService } from '../../../../data/repositories/repo-settings.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';
import { QuoteRequestsService } from '../../../quoterequests/quoterequest.service';
import { InvoicesService } from '../../invoices/invoices.service';
import { JobsFeatureAccessService } from '../../jobs.feature-access.service';
import { JobVariationsService } from './job-variations.service';

@UntilDestroy()
@Component({
    selector: 'app-job-variations',
    templateUrl: './job-variations.component.html',
    styleUrls: [
        '../../../../../../src/app/shared/composable-styles/input.scss',
        '../../../../../../src/app/shared/composable-styles/button.scss',
        './job-variations.component.scss',
    ],
    standalone: false,
})
export class JobVariationsComponent implements OnInit {
    // @ts-ignore TS2564
    @Input() job: Job;
    // @ts-ignore TS2564
    estimate: Estimate;
    lead: any;
    noticeModuleName = NoticeModuleName.Variations;
    noticeRetrievalMethod = NoticeRetrievalMethod.ObjectIds;
    hideNoticeActions = [NoticeAction.View];
    tableHeight = 'auto';
    translations = {
        variation: 'Variation',
        variationTitle: 'Variation #',
        adjustment: 'Adjustment',
    };

    showAdjustment = false;
    variationTypeLabel = { variation: 'Variation', adjustment: 'Adjustment' };
    estimateNotAccepted = false;
    isEmailVariationDisabled: boolean = false;
    modulePermitted: boolean = true;

    statusFilter = signal(['All']);
    searchText = signal<string>('');

    variations = signal<JobVariation[]>([]);

    rows = computed(() => {
        if (this.statusFilter().includes('All')) {
            return this.searchFilteredVariations();
        }

        const statusFilteredOrders = this.searchFilteredVariations().filter((variation) => {
            return this.statusFilter().includes(variation.Status);
        });

        return statusFilteredOrders;
    });

    searchFilteredVariations = computed(() => {
        const searchTerm = this.searchText();
        if (searchTerm.length > 0) {
            const searchFilteredOrders =
                this.variations().filter((variation) => {
                    return (
                        variation.VariationNo.toString().includes(searchTerm) || variation.Description.toLowerCase().includes(searchTerm)
                    );
                }) ?? [];

            return searchFilteredOrders;
        }

        return this.variations();
    });

    variationCounts = computed(() => {
        const counts = {
            all: 0,
            pending: 0,
            sent: 0,
            accepted: 0,
            rejected: 0,
        };

        this.searchFilteredVariations().forEach((p) => {
            counts.all++;
            switch (p.Status) {
                case 'Pending':
                    counts.pending++;
                    break;
                case 'Sent':
                    counts.sent++;
                    break;
                case 'Accepted':
                    counts.accepted++;
                    break;
                case 'Digitally Accepted':
                    counts.accepted++;
                    break;
                case 'Rejected':
                    counts.rejected++;
                    break;
            }
        });

        return counts;
    });

    constructor(
        private translateService: TranslateService,
        private leadsService: LeadsService,
        public permissionService: PermissionService,
        public featureService: FeatureService,
        private invoicesService: InvoicesService,
        public settings: RepoSettingsService,
        private variationsService: JobVariationsService,
        private sharedData: SharedDataService,
        private repoJobs: RepoJobsService,
        private quoteReqService: QuoteRequestsService,
        private dialogService: DialogService,
        private route: ActivatedRoute,
        private routerStateService: RouterStateService,
        private estimateEventsService: EstimateEventsService,
        private jobFeatureAccessService: JobsFeatureAccessService,
        private router: Router
    ) {
        this.showAdjustment =
            this.sharedData.UserData.tenant.BillingCountry === 'New Zealand' ||
            this.sharedData.UserData.tenant.BillingCountry === 'Australia';
        combineLatest([this.translateService.get('general.adjustment'), this.translateService.get('general.variation')])
            .pipe(first())
            .subscribe(([adjustment, variation]) => (this.variationTypeLabel = { adjustment, variation }));
        this.translations.variation = this.translateService.instant('general.variation');
        this.translations.variationTitle = `${this.translations.variation} #`;
        this.translations.adjustment = this.translateService.instant('general.adjustment');
    }

    ngOnInit(): void {
        this.modulePermitted = this.jobFeatureAccessService.canAccessJobVariations();
        if (!this.modulePermitted) return;

        this.loadLead();
        this.load();
        this.loadView();
        // Updates the view if the estimate status changes
        this.estimateEventsService.estimateStatusChanged$.pipe(untilDestroyed(this)).subscribe((e) => {
            if (this.job.Estimate && this.job.Estimate.ID_Estimate === e.id) {
                this.setEstimateNotAccepted();
            }
        });

        this.isEmailVariationDisabled =
            !this.permissionService.isAvailable('jobs.maintain-variations.print-email-variation') ||
            !this.featureService.isStateAvailable('jobs.maintain-variations.email-variation');
    }

    handleSearchInput(event: Event) {
        const searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.searchText.set(searchTerm);
    }

    loadLead() {
        if (this.job.ID_Lead) {
            this.leadsService.getLeadById(this.job.ID_Lead).then((result) => {
                if (result) {
                    this.lead = result.data['leadById'];
                }
            });
        }
    }

    load() {
        this.estimate = this.job.Estimate;
        this.setEstimateNotAccepted();
        this.loadVariations();
        this.loadVariationInvoices();
    }

    loadVariations() {
        this.variations.set(
            this.job.JobVariations?.filter((v) => {
                return !v.IsDeleted;
            }) ?? []
        );
    }

    loadVariationInvoices() {
        this.variations().forEach((v) => {
            const invoices: JobPayment[] = [];
            if (v.JobPayment) {
                invoices.push(v.JobPayment); //  old invoice method of directly applying a variation to an invoice
            } else {
                // @ts-ignore TS2532
                this.job.JobPayments.forEach((p) => {
                    if (!p.IsDeleted) {
                        if (
                            p.JobPaymentItems.some((pi) => {
                                return (
                                    pi.ID_Variation &&
                                    pi.ID_Variation === v.ID_Variation &&
                                    (pi.InvoiceTotal !== 0 || pi.InvoicePercentage !== 0)
                                );
                            })
                        ) {
                            invoices.push(p);
                        }
                    }
                });
            }
            v.invoices = invoices;
        });
    }

    loadView() {
        if (this.route.snapshot.queryParams.view) {
            const id = this.route.snapshot.queryParams.view;
            // @ts-ignore TS2532
            const variation = this.job.JobVariations.find((v) => {
                return !v.IsDeleted && v.ID_Variation === id;
            });
            if (variation) {
                this.viewVariation(variation);
            }
        }
    }

    setEstimateNotAccepted() {
        this.estimateNotAccepted = this.estimate && !this.estimate.IsAccepted;
    }

    setFilter(val: string[]) {
        this.statusFilter.set(val);
        this.load();
    }

    viewInvoice(invoice: JobPayment) {
        this.invoicesService
            .viewInvoice(invoice)
            .then(() => {
                this.loadVariationInvoices();
            })
            .catch(() => ({}));
    }

    // @ts-ignore TS7006
    viewVariation(variation) {
        this.variationsService.viewVariation(variation).then(
            () => {
                this.unloadView();
                // Status may have changed
                this.load();
            },
            () => {
                this.unloadView();
            }
        );
    }

    unloadView() {
        if (this.route.snapshot.queryParams.view) {
            const newParams = JSON.parse(JSON.stringify(this.route.snapshot.queryParams));
            delete newParams.view;
            this.routerStateService.reloadCurrent(newParams);
        }
    }

    addVariation() {
        this.variationsService.newVariation(this.job).then(
            () => {
                // Status may have changed
                this.load();
            },
            () => ({})
        );
    }

    deleteVariation(evt: MouseEvent, variation: JobVariation) {
        evt.stopPropagation();
        const params = {
            title: `Delete ${this.translations.variation}?`,
            text: "If you go ahead, you won't be able to get it back.",
        };
        this.dialogService.show(params).then(
            () => {
                variation.IsDeleted = true;
                this.repoJobs.Save(null, `${this.translations.variation} has been deleted`);
                this.quoteReqService.unlinkVariationFromRFQ(variation);
                this.load();
            },
            () => ({})
        );
    }

    printVariation(variation: JobVariation) {
        this.variationsService.printVariation(variation);
    }

    shareVariation(variation: JobVariation) {
        this.variationsService.shareVariation(variation);
    }
}
